import { create } from "zustand";
import type { Scene } from "../types/scene";
import type { KeyframeMap } from "./fileStore/types";
import { getFileState } from "./fileStore";
import { logger } from "../utils/logger";

// Generate a simple summary of changes between two snapshots
export interface HistorySnapshot {
  scene: Scene;
  keyframes: KeyframeMap;
}

export const generateChangeSummary = (before: HistorySnapshot, after: HistorySnapshot): string => {
  try {
    const changes: string[] = [];
    const beforeScene = before.scene;
    const afterScene = after.scene;

    // Compare body count (direct flat map access - no tree reconstruction)
    const beforeBodyCount = beforeScene.bodies ? Object.keys(beforeScene.bodies).length : 0;
    const afterBodyCount = afterScene.bodies ? Object.keys(afterScene.bodies).length : 0;
    if (beforeBodyCount !== afterBodyCount) {
      const diff = afterBodyCount - beforeBodyCount;
      changes.push(
        `${Math.abs(diff)} body${Math.abs(diff) !== 1 ? "ies" : ""} ${diff > 0 ? "added" : "removed"}`
      );
    }

    // Compare joints (direct flat map access - no tree reconstruction needed)
    const beforeJointCount = beforeScene.joints ? Object.keys(beforeScene.joints).length : 0;
    const afterJointCount = afterScene.joints ? Object.keys(afterScene.joints).length : 0;
    if (beforeJointCount !== afterJointCount) {
      const diff = afterJointCount - beforeJointCount;
      changes.push(
        `${Math.abs(diff)} joint${Math.abs(diff) !== 1 ? "s" : ""} ${diff > 0 ? "added" : "removed"}`
      );
    }

    // Compare materials
    const beforeMaterialCount = Object.keys(beforeScene.materials).length;
    const afterMaterialCount = Object.keys(afterScene.materials).length;
    if (beforeMaterialCount !== afterMaterialCount) {
      const diff = afterMaterialCount - beforeMaterialCount;
      changes.push(
        `${Math.abs(diff)} material${Math.abs(diff) !== 1 ? "s" : ""} ${diff > 0 ? "added" : "removed"}`
      );
    }

    // Compare textures
    const beforeTextureCount = Object.keys(beforeScene.textures).length;
    const afterTextureCount = Object.keys(afterScene.textures).length;
    if (beforeTextureCount !== afterTextureCount) {
      const diff = afterTextureCount - beforeTextureCount;
      changes.push(
        `${Math.abs(diff)} texture${Math.abs(diff) !== 1 ? "s" : ""} ${diff > 0 ? "added" : "removed"}`
      );
    }

    // Compare sites (direct flat map access - no tree reconstruction needed)
    const beforeSiteCount = beforeScene.sites ? Object.keys(beforeScene.sites).length : 0;
    const afterSiteCount = afterScene.sites ? Object.keys(afterScene.sites).length : 0;
    if (beforeSiteCount !== afterSiteCount) {
      const diff = afterSiteCount - beforeSiteCount;
      changes.push(
        `${Math.abs(diff)} site${Math.abs(diff) !== 1 ? "s" : ""} ${diff > 0 ? "added" : "removed"}`
      );
    }

    // Compare keyframe count
    const beforeKeyframeCount = Object.keys(before.keyframes || {}).length;
    const afterKeyframeCount = Object.keys(after.keyframes || {}).length;
    if (beforeKeyframeCount !== afterKeyframeCount) {
      const diff = afterKeyframeCount - beforeKeyframeCount;
      changes.push(
        `${Math.abs(diff)} pose${Math.abs(diff) !== 1 ? "s" : ""} ${diff > 0 ? "added" : "removed"}`
      );
    }

    // If no specific changes detected but snapshots are different, show generic message
    if (changes.length === 0) {
      changes.push("Properties modified");
    }

    return changes.slice(0, 2).join(", "); // Limit to 2 changes for brevity
  } catch (error) {
    logger.warn("[History] Error generating change summary:", error);
    return "Properties modified";
  }
};

// Flag to prevent history saves during undo/redo
let isUndoRedoInProgress = false;

function deepClone<T>(obj: T): T {
  try {
    if (typeof globalThis.structuredClone === "function") {
      return globalThis.structuredClone(obj);
    }
  } catch {
    // ignore
  }
  return JSON.parse(JSON.stringify(obj));
}

const hasOwn = (value: object, key: string) => Object.prototype.hasOwnProperty.call(value, key);

export const areHistorySnapshotsEqual = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true;
  if (typeof a !== typeof b || a === null || b === null) return false;
  if (typeof a !== "object") return false;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let index = 0; index < a.length; index += 1) {
      const key = String(index);
      if (hasOwn(a, key) !== hasOwn(b, key)) return false;
      if (!areHistorySnapshotsEqual(a[index], b[index])) return false;
    }
    return true;
  }

  const aRecord = a as Record<string, unknown>;
  const bRecord = b as Record<string, unknown>;
  const aKeys = Object.keys(aRecord);
  const bKeys = Object.keys(bRecord);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (!hasOwn(bRecord, key)) return false;
    if (!areHistorySnapshotsEqual(aRecord[key], bRecord[key])) return false;
  }
  return true;
};

type SceneWithRuntimeObjects = Scene & { sceneObjects?: unknown };

export const makeHistorySnapshot = (state: Scene): HistorySnapshot => {
  const { sceneObjects: _sceneObjects, ...rest } = state as SceneWithRuntimeObjects;
  const fileState = getFileState();
  return deepClone({
    scene: rest as Scene,
    keyframes: fileState.keyframes || {},
  } as HistorySnapshot);
};

const isHistorySnapshot = (value: Scene | HistorySnapshot): value is HistorySnapshot =>
  "scene" in value && "keyframes" in value;

interface HistoryEntry {
  snapshot: HistorySnapshot;
  summary: string;
  disabled?: boolean;
  error?: string;
}

interface HistoryState {
  past: HistoryEntry[];
  future: HistoryEntry[];
  maxHistorySize: number;
  // Grouping
  isGrouping: boolean;
  groupBaseline: HistorySnapshot | null;
}

interface HistoryActions {
  save: (state: Scene | HistorySnapshot, summaryOverride?: string) => void;
  undo: (
    getCurrentState: () => HistorySnapshot,
    setCurrentState: (state: HistorySnapshot) => void
  ) => void;
  redo: (
    getCurrentState: () => HistorySnapshot,
    setCurrentState: (state: HistorySnapshot) => void
  ) => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearHistory: () => void;
  setMaxHistorySize: (size: number) => void;
  // Grouping API
  beginGroup: (baseline: Scene) => void;
  endGroup: (finalState: Scene) => void;
}

export const createHistoryStore = () =>
  create<HistoryState & { actions: HistoryActions }>((set, get) => ({
    past: [],
    future: [],
    maxHistorySize: 50,
    isGrouping: false,
    groupBaseline: null,

    actions: {
      save: (state: Scene | HistorySnapshot, summaryOverride?: string) => {
        try {
          // Prevent saves during undo/redo operations or when replay chain exists
          const current = get();
          if (isUndoRedoInProgress) {
            logger.debug("[History] Save skipped - undo/redo in progress");
            return;
          }

          const snapshot = isHistorySnapshot(state) ? deepClone(state) : makeHistorySnapshot(state);

          // Grouping: just track baseline on first save in group
          if (current.isGrouping) {
            if (!current.groupBaseline) {
              set({ groupBaseline: snapshot });
            }
            return;
          }

          // Get the previous snapshot for comparison
          const lastEntry = current.past[current.past.length - 1];
          let summary = summaryOverride || "Initial state";

          if (lastEntry) {
            // Avoid pushing duplicates
            if (areHistorySnapshotsEqual(lastEntry.snapshot, snapshot)) {
              logger.debug("[History] Save skipped - no changes from last snapshot");
              return;
            }
            // Generate change summary
            summary = summaryOverride || generateChangeSummary(lastEntry.snapshot, snapshot);
          }

          const entry: HistoryEntry = { snapshot, summary };

          // Add the entry to past and clear future (new action)
          set((historyState) => ({
            past: [...historyState.past, entry].slice(-historyState.maxHistorySize),
            future: [],
          }));
        } catch (error) {
          logger.error("[History] Error in save function:", error);
          // Don't throw - just log and continue
        }
      },

      beginGroup: (baseline: Scene) => {
        const current = get();
        if (current.isGrouping) return;
        set({ isGrouping: true, groupBaseline: makeHistorySnapshot(baseline) });
      },

      endGroup: (finalState: Scene) => {
        const current = get();
        if (!current.isGrouping) return;

        const start = current.groupBaseline;
        const end = makeHistorySnapshot(finalState);
        set({ isGrouping: false, groupBaseline: null });

        // If no change across the group, do nothing
        if (start && !areHistorySnapshotsEqual(start, end)) {
          // Generate summary for the grouped action
          const summary = generateChangeSummary(start, end);

          const entry: HistoryEntry = { snapshot: start, summary };

          // Push the start snapshot so undo reverts the whole drag/gesture
          set((historyState) => ({
            past: [...historyState.past, entry].slice(-historyState.maxHistorySize),
            future: [],
          }));
        }
      },

      undo: (
        getCurrentState: () => HistorySnapshot,
        setCurrentState: (state: HistorySnapshot) => void
      ) => {
        try {
          const current = get();
          // Allow undo as long as there is at least one snapshot in the past
          if (current.past.length === 0) return;
          const previousEntry = current.past[current.past.length - 1];
          if (previousEntry?.disabled) return;

          logger.debug("[History] Undo - past length:", current.past.length);
          logger.debug("[History] Undo - future length:", current.future.length);

          // Set flag to prevent saves during undo
          isUndoRedoInProgress = true;
          logger.debug("[History] Undo - flag set to prevent saves");

          try {
            // Get the state to restore to (last in past)
            const currentState = getCurrentState();
            const currentEntry: HistoryEntry = { snapshot: currentState, summary: "Undone" };

            // Apply the previous state before moving stacks so failed entries can stay disabled.
            setCurrentState(previousEntry.snapshot);

            // Move current to future and trim past
            set((historyState) => ({
              past: historyState.past.slice(0, historyState.past.length - 1),
              future: [currentEntry, ...historyState.future],
            }));

            logger.debug("[History] Undo - after update, past length:", get().past.length);
            logger.debug("[History] Undo - after update, future length:", get().future.length);

            try {
              window.dispatchEvent(new CustomEvent("structure-changed"));
            } catch (error) {
              logger.warn("[History] Failed to dispatch structure-changed during undo", error);
            }
            logger.debug("[History] Undo - state applied");
          } finally {
            isUndoRedoInProgress = false;
            logger.debug("[History] Undo - flag reset");
          }
        } catch (error) {
          logger.error("[History] Error in undo function:", error);
          set((historyState) => {
            const lastIndex = historyState.past.length - 1;
            if (lastIndex < 0) return historyState;
            const past = historyState.past.map((entry, index) =>
              index === lastIndex
                ? { ...entry, disabled: true, error: "Could not restore state" }
                : entry
            );
            return { past };
          });
          isUndoRedoInProgress = false; // Reset flag on error
          throw error;
        }
      },

      redo: (
        getCurrentState: () => HistorySnapshot,
        setCurrentState: (state: HistorySnapshot) => void
      ) => {
        try {
          const current = get();
          if (current.future.length === 0) return;
          const nextEntry = current.future[0];
          if (nextEntry?.disabled) return;

          logger.debug("[History] Redo - past length:", current.past.length);
          logger.debug("[History] Redo - future length:", current.future.length);

          // Set flag to prevent saves during redo
          isUndoRedoInProgress = true;
          logger.debug("[History] Redo - flag set to prevent saves");

          try {
            // Get the state to restore to (first in future)
            const currentState = getCurrentState();
            const currentEntry: HistoryEntry = { snapshot: currentState, summary: "Redone" };

            // Apply the next state before moving stacks so failed entries can stay disabled.
            setCurrentState(nextEntry.snapshot);

            // Move current state to past
            set((historyState) => ({
              past: [...historyState.past, currentEntry],
              future: historyState.future.slice(1),
            }));

            logger.debug("[History] Redo - after update, past length:", get().past.length);
            logger.debug("[History] Redo - after update, future length:", get().future.length);
            logger.debug("[History] Redo - state applied");
          } finally {
            isUndoRedoInProgress = false;
            logger.debug("[History] Redo - flag reset");
          }
        } catch (error) {
          logger.error("[History] Error in redo function:", error);
          set((historyState) => {
            if (historyState.future.length === 0) return historyState;
            const future = historyState.future.map((entry, index) =>
              index === 0 ? { ...entry, disabled: true, error: "Could not restore state" } : entry
            );
            return { future };
          });
          isUndoRedoInProgress = false; // Reset flag on error
          throw error;
        }
      },

      canUndo: () => {
        const state = get();
        // Allow undo while there is at least one snapshot in the past (initial state included)
        return state.past.length > 0 && state.past[state.past.length - 1]?.disabled !== true;
      },

      canRedo: () => {
        const state = get();
        return state.future.length > 0 && state.future[0]?.disabled !== true;
      },

      clearHistory: () => {
        set({ past: [], future: [] });
      },

      setMaxHistorySize: (size: number) => {
        set((historyState) => ({
          maxHistorySize: size,
          past: historyState.past.slice(-size),
        }));
      },
    },
  }));

// Create the history store instance
const historyStore = createHistoryStore();

// Export hooks for easy access
export const useHistoryActions = () => historyStore((state) => state.actions);
/** Derive from stack heads so hooks do not depend on `actions` identity and only update when undo/redo availability changes. */
export const useCanUndo = () =>
  historyStore(
    (state) => state.past.length > 0 && state.past[state.past.length - 1]?.disabled !== true
  );
export const useCanRedo = () =>
  historyStore((state) => state.future.length > 0 && state.future[0]?.disabled !== true);
export const useHistorySize = () =>
  historyStore((state) => state.past.length + state.future.length);

export const useHistorySummaries = () =>
  historyStore((state) => {
    const pastSummary = state.past.length > 0 ? state.past[state.past.length - 1].summary : null;
    const futureSummary = state.future.length > 0 ? state.future[0].summary : null;
    return {
      past: pastSummary ? [pastSummary] : [],
      future: futureSummary ? [futureSummary] : [],
    };
  });

// Non-hook accessors for non-component files
export const getHistoryActions = () => historyStore.getState().actions;
export const getHistoryStore = () => historyStore;
// No type re-exports; use Scene directly
